import { deburr } from 'es-toolkit';
import { ownBooksFirst } from '@/utils/own-books';
import type { AuthUser } from '../context/AuthContext';
import { MembershipSource } from '../constants/membership-source';

/** One client the master account bar can switch into. */
export interface AccountRelation {
  id: string;
  /** What the bar shows — "Moje kancelář" for the master account's own books, the stored name otherwise. */
  name: string;
  isOwnBooks: boolean;
  /** `name` (and, for the own books, the stored name too) folded for search — computed once. */
  searchKey: string;
}

/** Case- and diacritics-insensitive form of a name or query (`Účto` → `ucto`). */
export function foldForSearch(value: string): string {
  return deburr(value).toLowerCase().trim();
}

/**
 * The clients `masterAccountId` lets this person into — their MASTER_ACCOUNT memberships granted by that master account. The
 * master account's own books come first, labelled `ownBooksLabel` (master-account-board plan §3.1), the rest by name.
 * A MASTER_ACCOUNT row from another master account is excluded: the bar switches within one master account only.
 */
export function accountRelationsOf(
  memberships: AuthUser['memberships'],
  masterAccountId: string,
  locale: string,
  ownBooksLabel: string
): AccountRelation[] {
  const collator = new Intl.Collator(locale, { sensitivity: 'base', numeric: true });
  const clients = (memberships ?? [])
    .filter(
      m =>
        m.source === MembershipSource.MASTER_ACCOUNT &&
        m.grantedByMasterAccount?.id === masterAccountId
    )
    .map(m => {
      const isOwnBooks = m.account.isMasterAccountOwnBooks === true;
      const name = isOwnBooks ? ownBooksLabel : m.account.name;
      return {
        id: m.account.id,
        name,
        isOwnBooks,
        // The stored name is the master account's own, so searching for the master account finds its own books too.
        searchKey: foldForSearch(isOwnBooks ? `${name} ${m.account.name}` : name)
      };
    })
    .sort((a, b) => collator.compare(a.name, b.name));
  return ownBooksFirst(clients, client => client.isOwnBooks);
}

export interface AccountRelationSearchResult {
  /** Matches to render, at most `limit` of them. */
  visible: AccountRelation[];
  /** Every match, including those beyond `limit`. */
  total: number;
}

/** Clients whose name contains `query`, ignoring case and diacritics, capped at `limit`. */
export function searchAccountRelations(
  clients: AccountRelation[],
  query: string,
  limit: number
): AccountRelationSearchResult {
  const folded = foldForSearch(query);
  const matches = folded ? clients.filter(c => c.searchKey.includes(folded)) : clients;
  return { visible: matches.slice(0, limit), total: matches.length };
}
